import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { BiblePassage } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { BibleTabs } from './Atlas';
import { bibleLink } from './BiblePage';

export const COLORS = [
  ['amarillo', 'Amarillo'],
  ['verde', 'Verde'],
  ['azul', 'Azul'],
  ['rosa', 'Rosa'],
  ['naranja', 'Naranja'],
  ['morado', 'Morado'],
] as const;

/** Agrupa versículos seguidos: [1,2,3,5] → ["1-3", "5"]. */
export function verseRanges(verses: number[]): [number, number][] {
  const out: [number, number][] = [];
  for (const v of verses) {
    const last = out.at(-1);
    if (last && last[1] === v - 1) last[1] = v;
    else out.push([v, v]);
  }
  return out;
}
const chapterOf = (p: BiblePassage) => p.label.replace(/:.*$/, '');
const rangeRef = (p: BiblePassage, [a, b]: [number, number]) => `${chapterOf(p)}:${a}${b !== a ? `-${b}` : ''}`;

/** Barra que aparece al tocar versículos: resaltar, memorizar o crear imagen. */
export function VerseToolbar({ passage: p, verses, onDone }: { passage: BiblePassage; verses: number[]; onDone: (message: string | null) => void }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ranges = verseRanges(verses);
  const label = ranges.map((r) => rangeRef(p, r)).join(', ');

  async function run(fn: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone(message);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  const paint = (color: string | null) =>
    run(() => api.put('/bible/me/highlights', { book: p.reference.book, chapter: p.reference.chapter, verses, color }), color ? `${label}: resaltado.` : `${label}: sin color.`);

  return (
    <div className="verse-toolbar" role="toolbar" aria-label={`Acciones para ${label}`}>
      <p className="verse-toolbar__label">{label}</p>
      <div className="verse-toolbar__colors">
        {COLORS.map(([key, name]) => (
          <button key={key} type="button" className={`swatch hl--${key}`} aria-label={`Resaltar en ${name.toLowerCase()}`} title={name} disabled={busy} onClick={() => void paint(key)} />
        ))}
        <button type="button" className="swatch swatch--none" aria-label="Quitar color" title="Quitar color" disabled={busy} onClick={() => void paint(null)}>
          ✕
        </button>
      </div>
      <div className="actions actions--wrap">
        <Button
          variant="secondary"
          busy={busy}
          onClick={() => void run(() => Promise.all(ranges.map((r) => api.post('/bible/me/memory/plans', { ref: rangeRef(p, r), days: 1 }))), `${label}: agregado a Memorizar.`)}
        >
          Memorizar
        </Button>
        <Button variant="secondary" onClick={() => navigate(`/app/biblia/imagen?ref=${encodeURIComponent(rangeRef(p, [verses[0]!, verses.at(-1)!]))}`)}>
          Crear imagen
        </Button>
        <Button variant="ghost" onClick={() => onDone(null)}>
          Cancelar
        </Button>
      </div>
      {error ? <Alert>{error}</Alert> : null}
    </div>
  );
}

/** Mis resaltados, filtrables por color. */
export function HighlightsCard() {
  const [color, setColor] = useState<string>('');
  const list = useAsync((signal) => api.get<{ items: { ref: string; color: string }[] }>(`/bible/me/highlights${color ? `?color=${color}` : ''}`, signal).then((r) => r.items), [color]);
  if (list.status === 'ready' && list.data.length === 0 && !color) return null;
  return (
    <Card title="Mis resaltados">
      <div className="chips">
        <button type="button" className={`chip${color ? '' : ' chip--active'}`} onClick={() => setColor('')}>
          Todos
        </button>
        {COLORS.map(([key, name]) => (
          <button key={key} type="button" className={`chip${color === key ? ' chip--active' : ''}`} onClick={() => setColor(key)}>
            <span className={`dot hl--${key}`} aria-hidden="true" /> {name}
          </button>
        ))}
      </div>
      {list.status === 'ready' ? (
        list.data.length === 0 ? (
          <p className="muted small">Nada resaltado en ese color.</p>
        ) : (
          <ul className="list">
            {list.data.slice(0, 50).map((h) => (
              <li key={`${h.ref}-${h.color}`} className="list__item">
                <span className={`dot hl--${h.color}`} aria-hidden="true" /> <Link to={bibleLink(h.ref)}>{h.ref}</Link>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Card>
  );
}

// ── Memorizar ──────────────────────────────────────────────────────────────

type MemoryPlan = { id: string; title: string; ref: string; days: number; startedOn: string; challengeId: string | null; total: number; unlocked: number; mastered: number; nextUnlock: string | null };
type MemoryState = { today: string; streak: number; reviewedToday: boolean; due: number; plans: MemoryPlan[] };
type DueCard = { id: string; ref: string; text: string; plan: string; box: number; isNew: boolean };
type Challenge = { id: string; title: string; audience: string | null; ref: string; days: number; startsOn: string; verses: number; joined: boolean; participants: number };

const shortDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('es', { day: 'numeric', month: 'short' });

export function MemoryPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const state = useAsync((signal) => api.get<MemoryState>('/bible/me/memory', signal), []);
  const [reviewing, setReviewing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const challenges = useAsync(
    (signal) => (churchId ? api.get<ChallengeList>(`/bible/churches/${churchId}/memory-challenges`, signal) : Promise.resolve(null)),
    [churchId],
  );
  // Con retos abiertos, la tarjeta sube justo debajo de «Repasar ahora»; sin retos, queda al final.
  const openChallenges = challenges.status === 'ready' && (challenges.data?.items.length ?? 0) > 0;
  const challengesCard =
    churchId && challenges.status === 'ready' && challenges.data ? (
      <ChallengesCard churchId={churchId} data={challenges.data} reload={challenges.reload} onJoined={() => state.reload()} />
    ) : null;

  if (reviewing) {
    return (
      <div className="page page--narrow bible-wide">
        <header className="page__header">
          <h1>Repaso de hoy</h1>
        </header>
        <ReviewSession
          onFinish={(n) => {
            setReviewing(false);
            setNotice(n ? `¡Bien hecho! Repasaste ${n} ${n === 1 ? 'versículo' : 'versículos'}.` : null);
            state.reload();
          }}
        />
      </div>
    );
  }

  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="memorizar" />
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {state.status === 'loading' ? <Loading /> : null}
      {state.status === 'error' ? <ErrorState error={state.error} onRetry={state.reload} /> : null}
      {state.status === 'ready' ? (
        <>
          <div className="stat-row">
            <div className="stat">
              <span className="stat__value">{state.data.due}</span>
              <span className="stat__label">Por repasar hoy</span>
            </div>
            <div className="stat">
              <span className="stat__value">{state.data.streak}</span>
              <span className="stat__label">{state.data.streak === 1 ? 'Día seguido' : 'Días seguidos'}</span>
            </div>
            <div className="stat">
              <span className="stat__value">{state.data.plans.reduce((n, p) => n + p.mastered, 0)}</span>
              <span className="stat__label">Aprendidos</span>
            </div>
          </div>
          {state.data.due > 0 ? (
            <Button className="btn--block" onClick={() => setReviewing(true)}>
              Repasar ahora ({state.data.due})
            </Button>
          ) : state.data.plans.length ? (
            <Alert tone="success">{state.data.reviewedToday ? '¡Listo por hoy! Vuelve mañana para seguir.' : 'Hoy no tienes versículos por repasar.'}</Alert>
          ) : null}
          {openChallenges ? challengesCard : null}
          <NewPlanCard onCreated={state.reload} />
          {state.data.plans.length ? (
            <Card title="Mis planes">
              <ul className="list">
                {state.data.plans.map((p) => (
                  <PlanRow key={p.id} plan={p} onRemoved={state.reload} />
                ))}
              </ul>
            </Card>
          ) : (
            <EmptyState title="Aún no memorizas ningún pasaje.">Empieza con un plan (por ejemplo, Salmos 23 en 30 días) o toca un versículo al leer y elige «Memorizar».</EmptyState>
          )}
        </>
      ) : null}
      {!openChallenges || state.status !== 'ready' ? challengesCard : null}
    </div>
  );
}

function PlanRow({ plan: p, onRemoved }: { plan: MemoryPlan; onRemoved: () => void }) {
  const pct = p.total ? Math.round((p.mastered / p.total) * 100) : 0;
  return (
    <li className="list__item memory-plan">
      <div className="memory-plan__head">
        <strong>{p.title}</strong>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            if (window.confirm(`¿Quitar «${p.title}»? Se borra su avance.`)) void api.delete(`/bible/me/memory/plans/${p.id}`).then(onRemoved);
          }}
        >
          Quitar
        </button>
      </div>
      <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Avance de ${p.title}`}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <span className="muted small">
        {p.mastered} de {p.total} aprendidos · {p.unlocked} abiertos{p.nextUnlock ? ` · el siguiente se abre el ${shortDate(p.nextUnlock)}` : ''}
      </span>
    </li>
  );
}

function NewPlanCard({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.post('/bible/me/memory/plans', { ref: String(f.get('ref')), days: Number(f.get('days') || 1) });
      setOpen(false);
      onCreated();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  if (!open)
    return (
      <div className="actions">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          Nuevo plan
        </Button>
      </div>
    );
  return (
    <Card title="Nuevo plan">
      <form className="form" onSubmit={submit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <Field label="Pasaje" name="ref" required placeholder="Salmos 23, Juan 3:16, Filipenses 4:6-7" />
        <Field label="¿En cuántos días?" name="days" type="number" min={1} max={365} defaultValue={30} hint="KEORA abre un versículo nuevo cada tanto y te recuerda repasar cada día." />
        <div className="actions">
          <Button type="submit" busy={busy}>
            Crear plan
          </Button>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** Pistas: solo la primera letra de cada palabra («P d t m a D a m»). */
export const firstLetters = (text: string) => text.replace(/([A-Za-zÁÉÍÓÚÑÜáéíóúñü])[A-Za-zÁÉÍÓÚÑÜáéíóúñü]*/g, '$1');

function ReviewSession({ onFinish }: { onFinish: (reviewed: number) => void }) {
  const due = useAsync((signal) => api.get<{ items: DueCard[] }>('/bible/me/memory/due', signal).then((r) => r.items), []);
  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState<'ref' | 'hint' | 'text'>('ref');
  const [done, setDone] = useState(0);
  const [busy, setBusy] = useState(false);
  if (due.status === 'loading') return <Loading />;
  if (due.status === 'error') return <ErrorState error={due.error} onRetry={due.reload} />;
  const card = due.data[index];
  if (!card) {
    return (
      <Card>
        <p className="memory-card__done">¡Terminaste el repaso de hoy! 🎉</p>
        <Button onClick={() => onFinish(done)}>Volver</Button>
      </Card>
    );
  }
  async function grade(result: 'bien' | 'casi' | 'mal') {
    setBusy(true);
    try {
      await api.post(`/bible/me/memory/cards/${card!.id}/review`, { result });
      setDone((n) => n + 1);
      setIndex((i) => i + 1);
      setMode('ref');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <p className="small">
        <button type="button" className="link-button" onClick={() => onFinish(done)}>
          ← Salir del repaso
        </button>
      </p>
      <p className="muted small">
        {index + 1} de {due.data.length} · {card.plan}
        {card.isNew ? ' · nuevo' : ''}
      </p>
      <p className="memory-card__ref">{card.ref}</p>
      {mode === 'ref' ? (
        <>
          <p className="muted">{card.isNew ? 'Léelo con calma varias veces y trata de decirlo sin ver.' : 'Dilo de memoria (en voz alta o en tu mente).'}</p>
          <div className="actions actions--wrap">
            <Button onClick={() => setMode('text')}>Mostrar el texto</Button>
            <Button variant="secondary" onClick={() => setMode('hint')}>
              Dame una pista
            </Button>
          </div>
        </>
      ) : null}
      {mode === 'hint' ? (
        <>
          <p className="memory-card__text memory-card__text--hint">{firstLetters(card.text)}</p>
          <Button onClick={() => setMode('text')}>Mostrar el texto</Button>
        </>
      ) : null}
      {mode === 'text' ? (
        <>
          <p className="memory-card__text">{card.text}</p>
          <p className="small">¿Cómo te fue?</p>
          <div className="actions actions--wrap">
            <Button busy={busy} onClick={() => void grade('bien')}>
              Lo sabía
            </Button>
            <Button variant="secondary" busy={busy} onClick={() => void grade('casi')}>
              Casi
            </Button>
            <Button variant="ghost" busy={busy} onClick={() => void grade('mal')}>
              No lo sabía
            </Button>
          </div>
        </>
      ) : null}
      <button type="button" className="btn btn--ghost" onClick={() => onFinish(done)}>
        Terminar por hoy
      </button>
    </Card>
  );
}

type ChallengeList = { canManage: boolean; items: Challenge[] };

function ChallengesCard({ churchId, data, reload, onJoined }: { churchId: string; data: ChallengeList; reload: () => void; onJoined: () => void }) {
  const base = `/bible/churches/${churchId}/memory-challenges`;
  const list = { data, reload };
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!list.data.canManage && list.data.items.length === 0) return null;

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    setError(null);
    try {
      await api.post(base, { title: String(f.get('title')), audience: String(f.get('audience') ?? ''), ref: String(f.get('ref')), days: Number(f.get('days') || 30), startsOn: String(f.get('startsOn') || '') || null });
      setCreating(false);
      list.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <Card title="Retos de la iglesia" action={list.data.canManage && !creating ? <Button variant="ghost" onClick={() => setCreating(true)}>Nuevo reto</Button> : undefined}>
      {error ? <Alert>{error}</Alert> : null}
      {creating ? (
        <form className="form" onSubmit={create} noValidate>
          <Field label="Nombre del reto" name="title" required placeholder="Niños: Salmo 23 en un mes" />
          <Field label="¿Para quién? (opcional)" name="audience" placeholder="Niños, jóvenes, toda la iglesia…" />
          <Field label="Pasaje" name="ref" required placeholder="Salmos 23" />
          <div className="form__row">
            <Field label="Días" name="days" type="number" min={1} max={365} defaultValue={30} />
            <Field label="Empieza" name="startsOn" type="date" />
          </div>
          <div className="actions">
            <Button type="submit">Publicar reto</Button>
            <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : null}
      {list.data.items.length === 0 ? <p className="muted small">Crea un reto para que tu iglesia (o los niños) memorice un pasaje junta.</p> : null}
      <ul className="list">
        {list.data.items.map((c) => (
          <li key={c.id} className="list__item">
            <strong>{c.title}</strong>
            <span className="muted small block">
              {c.ref} · {c.verses} {c.verses === 1 ? 'versículo' : 'versículos'} en {c.days} días · desde {shortDate(c.startsOn)}
              {c.audience ? ` · ${c.audience}` : ''} · {c.participants} {c.participants === 1 ? 'participante' : 'participantes'}
            </span>
            <div className="actions actions--wrap">
              {c.joined ? (
                <span className="badge badge--active">Estás participando</span>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() =>
                    void api
                      .post(`${base}/${c.id}/join`)
                      .then(() => (list.reload(), onJoined()))
                      .catch((e) => setError(errorMessage(e)))
                  }
                >
                  Unirme al reto
                </Button>
              )}
              {list.data.canManage ? (
                <Button variant="ghost" onClick={() => setOpen(open === c.id ? null : c.id)}>
                  {open === c.id ? 'Ocultar avance' : 'Ver avance'}
                </Button>
              ) : null}
            </div>
            {open === c.id ? <ChallengeProgress url={`${base}/${c.id}`} onClosed={list.reload} /> : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ChallengeProgress({ url, onClosed }: { url: string; onClosed: () => void }) {
  const data = useAsync(
    (signal) => api.get<{ participants: { personId: string; name: string; total: number; mastered: number; lastReviewed: string | null }[] }>(url, signal),
    [url],
  );
  if (data.status !== 'ready') return <Loading />;
  return (
    <div className="challenge-progress">
      {data.data.participants.length === 0 ? <p className="muted small">Nadie se ha unido todavía.</p> : null}
      <ol className="list">
        {data.data.participants.map((p) => (
          <li key={p.personId} className="list__item">
            <strong>{p.name}</strong>{' '}
            <span className="muted small">
              · {p.mastered}/{p.total} aprendidos{p.lastReviewed ? ` · repasó el ${shortDate(p.lastReviewed)}` : ' · aún no repasa'}
            </span>
          </li>
        ))}
      </ol>
      <Button
        variant="ghost"
        onClick={() => {
          if (window.confirm('¿Cerrar este reto? Ya no se podrán unir más personas.')) void api.post(`${url}/close`).then(onClosed);
        }}
      >
        Cerrar reto
      </Button>
    </div>
  );
}

// ── Imagen del versículo ─────────────────────────────────────────────────────

const THEMES = [
  { key: 'amanecer', name: 'Amanecer', bg: ['#f6d365', '#fda085'], fg: '#2b1d0e' },
  { key: 'mar', name: 'Mar', bg: ['#1f3440', '#2f6f8f'], fg: '#ffffff' },
  { key: 'bosque', name: 'Bosque', bg: ['#134e3a', '#3f8f5f'], fg: '#ffffff' },
  { key: 'lavanda', name: 'Lavanda', bg: ['#c3b1e1', '#f3e7ff'], fg: '#2d1b4e' },
  { key: 'noche', name: 'Noche', bg: ['#0f172a', '#334155'], fg: '#f8fafc' },
  { key: 'claro', name: 'Claro', bg: ['#f5f6f8', '#ffffff'], fg: '#1f3440' },
] as const;
const FORMATS = { cuadrada: [1080, 1080], historia: [1080, 1920] } as const;

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(/\s+/)) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

/** Imagen bonita del versículo con el logo de la iglesia, para compartir en WhatsApp o Facebook. */
export function VerseImagePage() {
  const [params, setParams] = useSearchParams();
  const me = useMe();
  const ref = params.get('ref') ?? 'Juan 3:16';
  const passage = useAsync((signal) => api.get<BiblePassage>(`/bible/passage?ref=${encodeURIComponent(ref)}`, signal), [ref]);
  const church = useAsync(
    (signal) =>
      me.activeChurchId ? api.get<{ name: string; logoUrl: string | null }>(`/multichurch/public/join/${me.activeChurchId}`, signal) : Promise.resolve(null),
    [me.activeChurchId],
  );
  const [theme, setTheme] = useState<(typeof THEMES)[number]['key']>('amanecer');
  const [format, setFormat] = useState<keyof typeof FORMATS>('cuadrada');
  const canvas = useRef<HTMLCanvasElement>(null);
  const [logo, setLogo] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const logoUrl = church.status === 'ready' ? church.data?.logoUrl ?? null : null;
  useEffect(() => {
    if (!logoUrl) return setLogo(null);
    const img = new Image();
    img.onload = () => setLogo(img);
    img.onerror = () => setLogo(null);
    img.src = logoUrl;
  }, [logoUrl]);

  const text = passage.status === 'ready' ? passage.data.verses.map((v) => v.text).join(' ') : '';
  const label = passage.status === 'ready' ? passage.data.label : ref;
  const churchName = church.status === 'ready' ? church.data?.name ?? null : null;
  const t = useMemo(() => THEMES.find((x) => x.key === theme)!, [theme]);

  useEffect(() => {
    const c = canvas.current;
    const ctx = c?.getContext?.('2d');
    if (!c || !ctx || !text) return;
    const [w, h] = FORMATS[format];
    c.width = w;
    c.height = h;
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, t.bg[0]);
    g.addColorStop(1, t.bg[1]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = t.fg;
    ctx.textAlign = 'center';
    const pad = 110;
    const footer = 190;
    // Tamaño de letra que quepa en el espacio disponible.
    let size = 76;
    let lines: string[] = [];
    for (; size >= 30; size -= 2) {
      ctx.font = `${size}px Georgia, 'Times New Roman', serif`;
      lines = wrap(ctx, `“${text}”`, w - pad * 2);
      if (lines.length * size * 1.35 <= h - pad * 2 - footer) break;
    }
    const lh = size * 1.35;
    const top = (h - footer - lines.length * lh) / 2 + size;
    lines.forEach((l, i) => ctx.fillText(l, w / 2, top + i * lh));
    ctx.font = `bold ${Math.round(size * 0.62)}px system-ui, sans-serif`;
    ctx.fillText(label, w / 2, top + lines.length * lh + size * 0.4);
    // Pie: logo y nombre de la iglesia.
    ctx.font = '600 34px system-ui, sans-serif';
    const name = churchName ?? 'KEORA';
    const y = h - 90;
    if (logo) {
      const s = 84;
      const tw = ctx.measureText(name).width;
      const x0 = w / 2 - (s + 20 + tw) / 2;
      ctx.drawImage(logo, x0, y - s / 2 - 10, s, s);
      ctx.textAlign = 'left';
      ctx.fillText(name, x0 + s + 20, y);
    } else {
      ctx.fillText(name, w / 2, y);
    }
  }, [text, label, t, format, logo, churchName]);

  async function blob(): Promise<Blob | null> {
    return new Promise((resolve) => canvas.current?.toBlob((b) => resolve(b), 'image/png') ?? resolve(null));
  }
  const fileName = `${label.replace(/[^\wáéíóúñ]+/gi, '-')}.png`;

  async function share() {
    setError(null);
    const b = await blob();
    if (!b) return;
    const file = new File([b], fileName, { type: 'image/png' });
    const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
    if (nav.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], text: `${label}` }).catch(() => undefined);
    } else {
      download(b);
      setError('Tu navegador no permite compartir directo: se descargó la imagen para que la envíes por WhatsApp o Facebook.');
    }
  }
  function download(b?: Blob | null) {
    const go = (x: Blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(x);
      a.download = fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    };
    if (b) go(b);
    else void blob().then((x) => x && go(x));
  }

  return (
    <div className="page page--narrow bible-wide">
      <p className="small">
        <Link to={bibleLink(ref)}>← {ref}</Link>
      </p>
      <header className="page__header">
        <h1>Imagen del versículo</h1>
      </header>
      <form
        className="bible__search"
        onSubmit={(e) => {
          e.preventDefault();
          const next = String(new FormData(e.currentTarget).get('versiculo') ?? '').trim();
          if (next && next !== ref) setParams({ ref: next }, { replace: true });
        }}
      >
        <Field label="Versículo" name="versiculo" key={ref} defaultValue={ref} placeholder="Salmos 23:1" autoComplete="off" />
        <Button type="submit" variant="secondary">Cambiar</Button>
      </form>
      {passage.status === 'error' ? <ErrorState error={passage.error} /> : null}
      <Card>
        <div className="chips" role="radiogroup" aria-label="Estilo">
          {THEMES.map((x) => (
            <button key={x.key} type="button" role="radio" aria-checked={theme === x.key} className={`chip${theme === x.key ? ' chip--active' : ''}`} onClick={() => setTheme(x.key)}>
              {x.name}
            </button>
          ))}
        </div>
        <div className="chips" role="radiogroup" aria-label="Formato">
          <button type="button" role="radio" aria-checked={format === 'cuadrada'} className={`chip${format === 'cuadrada' ? ' chip--active' : ''}`} onClick={() => setFormat('cuadrada')}>
            Cuadrada (publicación)
          </button>
          <button type="button" role="radio" aria-checked={format === 'historia'} className={`chip${format === 'historia' ? ' chip--active' : ''}`} onClick={() => setFormat('historia')}>
            Vertical (estado o historia)
          </button>
        </div>
        {passage.status === 'loading' ? <Loading /> : null}
        <canvas ref={canvas} className="verse-image" aria-label={`Imagen de ${label}`} role="img" />
        {!logoUrl && me.activeChurchId ? <p className="muted small">Tu iglesia aún no sube su logo; la administración puede hacerlo en «Sitio de la iglesia».</p> : null}
        {error ? <Alert tone="info">{error}</Alert> : null}
        <div className="actions actions--wrap">
          <Button onClick={() => void share()}>Compartir</Button>
          <Button variant="secondary" onClick={() => download()}>
            Descargar
          </Button>
        </div>
      </Card>
    </div>
  );
}
